import React, {useState, useRef, useEffect, KeyboardEvent} from 'react'
// @ts-ignore
import Tooltip from 'react-bootstrap/Tooltip'
// @ts-ignore
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {KTSVG} from '../../../../_metronic/helpers'
import {SHOW_ERROR, SHOW_SUCCESS} from '../../../../util/toast'
import {getEmailRecipients, saveEmailRecipients} from '../../../../api/endpoints/statistic'

type Frequency = 'daily' | 'weekly' | 'monthly'

const FREQUENCY_OPTIONS: {value: Frequency; label: string}[] = [
  {value: 'daily', label: 'Daily'},
  {value: 'weekly', label: 'Weekly'},
  {value: 'monthly', label: 'Monthly'},
]

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

const EmailReportConfig: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [frequency, setFrequency] = useState<Frequency>('daily')
  const [isFetching, setIsFetching] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await getEmailRecipients()
        const data = res?.data?.data
        if (Array.isArray(data) && data.length > 0) {
          setEmails(data.map((item: {email: string}) => item.email))
          setFrequency(data[0].frequency as Frequency)
        }
      } catch {
        // silently ignore — form stays at defaults
      } finally {
        setIsFetching(false)
      }
    }
    fetchConfig()
  }, [])

  const addEmail = (raw: string) => {
    const email = raw.trim().replace(/,+$/, '')
    if (!email) return
    if (!isValidEmail(email)) {
      SHOW_ERROR(true, `"${email}" is not a valid email address`)
      return
    }
    if (emails.includes(email)) {
      SHOW_ERROR(true, `"${email}" is already added`)
      return
    }
    setEmails((prev) => [...prev, email])
    setInputValue('')
  }

  const removeEmail = (index: number) => {
    setEmails((prev) => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails.length - 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    const parts = pasted.split(/[\s,;]+/).filter(Boolean)
    const added: string[] = []
    const invalid: string[] = []
    parts.forEach((p) => {
      if (!isValidEmail(p)) { invalid.push(p); return }
      if (emails.includes(p) || added.includes(p)) return
      added.push(p)
    })
    if (invalid.length) SHOW_ERROR(true, `Skipped invalid: ${invalid.join(', ')}`)
    if (added.length) setEmails((prev) => [...prev, ...added])
  }

  const handleSave = async () => {
    if (emails.length === 0) {
      SHOW_ERROR(true, 'Please add at least one email address')
      return
    }
    try {
      setIsSaving(true)
      await saveEmailRecipients({emails, frequency})
      SHOW_SUCCESS(true, 'Email report settings saved successfully')
    } catch {
      SHOW_ERROR(true, 'Failed to save settings. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='card card-xl-stretch mb-xl-8'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Email Report Settings</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            Schedule summary stats to be delivered by email
          </span>
        </h3>
        <div className='card-toolbar'>
          <KTSVG
            path='media/icons/duotune/communication/com002.svg'
            className='svg-icon-2 svg-icon-primary'
          />
        </div>
      </div>

      <div className='card-body pt-3'>
        {isFetching ? (
          <div className='d-flex flex-column gap-4'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='bg-light rounded-2 animate__animated animate__pulse' style={{height: 40}} />
            ))}
          </div>
        ) : (
        <>
        {/* Frequency */}
        <div className='mb-7'>
          <label className='form-label fw-bold fs-6 text-gray-700 d-flex align-items-center gap-2'>
            Report Frequency
            <OverlayTrigger
              placement='right'
              overlay={
                <Tooltip id='frequency-tooltip'>
                  How often the summary stats email will be sent to the recipients.
                  <br />
                  <strong>Daily</strong> — every day at a scheduled time
                  <br />
                  <strong>Weekly</strong> — once a week
                  <br />
                  <strong>Monthly</strong> — once a month
                </Tooltip>
              }
            >
              <span style={{cursor: 'pointer', lineHeight: 1}}>
                <KTSVG
                  path='media/icons/duotune/general/gen046.svg'
                  className='svg-icon-4 svg-icon-gray-400'
                />
              </span>
            </OverlayTrigger>
          </label>
          <div className='d-flex gap-3'>
            {FREQUENCY_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`d-flex align-items-center gap-2 px-5 py-3 rounded-2 cursor-pointer border fw-semibold fs-6 ${
                  frequency === opt.value
                    ? 'border-primary bg-light-primary text-primary'
                    : 'border-gray-300 text-gray-600'
                }`}
                style={{cursor: 'pointer'}}
              >
                <input
                  type='radio'
                  name='frequency'
                  value={opt.value}
                  checked={frequency === opt.value}
                  onChange={() => setFrequency(opt.value)}
                  className='form-check-input mt-0'
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Email recipients */}
        <div className='mb-7'>
          <label className='form-label fw-bold fs-6 text-gray-700'>
            Recipient Emails
            <span className='text-muted fw-normal ms-2 fs-7'>
              — press Enter or comma to add
            </span>
          </label>

          {/* Tag input container */}
          <div
            className='form-control d-flex flex-wrap align-items-center gap-2 p-3'
            style={{minHeight: '52px', cursor: 'text'}}
            onClick={() => inputRef.current?.focus()}
          >
            {emails.map((email, i) => (
              <span
                key={i}
                className='badge badge-light-primary d-flex align-items-center gap-1 px-3 py-2 fs-7 fw-semibold'
              >
                {email}
                <span
                  className='ms-1'
                  style={{cursor: 'pointer', lineHeight: 1}}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeEmail(i)
                  }}
                >
                  <KTSVG
                    path='media/icons/duotune/general/gen040.svg'
                    className='svg-icon-7 svg-icon-primary'
                  />
                </span>
              </span>
            ))}

            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => inputValue && addEmail(inputValue)}
              placeholder={emails.length === 0 ? 'e.g. admin@example.com' : ''}
              className='border-0 outline-0 flex-grow-1 fs-6 text-gray-700'
              style={{minWidth: '200px', outline: 'none', background: 'transparent'}}
            />
          </div>

          {emails.length > 0 && (
            <div className='form-text text-muted mt-2'>
              {emails.length} recipient{emails.length > 1 ? 's' : ''} added
            </div>
          )}
        </div>

        {/* Actions */}
        <div className='d-flex justify-content-end gap-3'>
          <button
            type='button'
            className='btn btn-light btn-sm fw-bold'
            disabled={isSaving}
            onClick={() => {
              setEmails([])
              setInputValue('')
              setFrequency('daily')
            }}
          >
            Reset
          </button>
          <button
            type='button'
            className='btn btn-primary btn-sm fw-bold'
            disabled={isSaving}
            onClick={handleSave}
          >
            {isSaving ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' role='status' />
                Saving...
              </>
            ) : (
              <>
                <KTSVG
                  path='media/icons/duotune/general/gen016.svg'
                  className='svg-icon-4 me-1'
                />
                Save Settings
              </>
            )}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  )
}

export default EmailReportConfig
